import React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { Box, useMediaQuery } from "@mui/material";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  border:'none',
  borderRadius: theme.shape.borderRadius,
  boxShadow: "none",
  marginBottom: theme.spacing(1),
  "&:before": {
    display: "none",
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(1),
  "& .MuiAccordionSummary-content": {
    margin: 0,
    alignItems: "center",
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  borderTop: `none`,
}));

export default function WebshopAccordion({ data }) {
  const isSmallScreen = useMediaQuery('sm');

  // Build name → info pairs dynamically
  const formattedData = data.reduce((acc, item) => {
    if (item.fieldname.startsWith("additionalname") && item.data?.trim()) {
      const index = item.fieldname.replace("additionalname", "");

      const infoItem = data.find(
        (el) => el.fieldname === `additionalinfo${index}` && el.data?.trim()
      );

      acc.push({
        heading: item.data, // ✅ Use data, not fieldname
        description: infoItem?.data || "",
      });
    }
    return acc;
  }, []);

  return (
    <div>
      {formattedData.map((item, index) => (
        <Accordion
          key={index}
          disableGutters
          elevation={0}
          sx={{
            background: "#ffffff",
            borderRadius: 3,
            mb: 2,
            overflow: "hidden",
            border: "1px solid #eef2f7",
            transition: "all 0.3s ease",
            "&:before": { display: "none" },
            "&:hover": {
              borderColor: "#F37120",
              boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
              transform: "translateY(-2px)",
            },
          }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon
                sx={{
                  fontSize: 22,
                  color: "#94a3b8",
                  transition: "all 0.3s ease",
                  ".Mui-expanded &": {
                    transform: "rotate(180deg)",
                    color: "#F37120",
                  },
                }}
              />
            }
            sx={{
              px: 3,
              py: 2.2,
              alignItems: "center",
              "& .MuiAccordionSummary-content": {
                margin: 0,
              },
              "&.Mui-expanded": {
                backgroundColor: "#fff8f3",
              },
            }}
          >
            {/* Animated Accent Line */}
            <Box
              sx={{
                width: 4,
                height: 26,
                borderRadius: 2,
                background: "linear-gradient(180deg, #F37120, #ff9a3c)",
                mr: 2,
                transition: "all 0.3s ease",
                ".Mui-expanded &": {
                  height: 34,
                },
              }}
            />
  
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: isSmallScreen ? "1rem" : "1.2rem",
                color: "#0f172a",
                letterSpacing: "0.4px",
                transition: "color 0.3s ease",
                ".Mui-expanded &": {
                  color: "#F37120",
                },
              }}
            >
              {item.heading}
            </Typography>
          </AccordionSummary>
  
          <AccordionDetails
            sx={{
              px: 6,
              pb: 3,
              pt: 1,
              fontSize: isSmallScreen ? "0.92rem" : "1rem",
              color: "#475569",
              lineHeight: 1.8,
              whiteSpace: "pre-line",
              animation: "fadeIn 0.3s ease-in-out",
              "@keyframes fadeIn": {
                from: { opacity: 0, transform: "translateY(5px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            {item.description}
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
}

